import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMe } from '../../hooks/use-auth';
import { useProfileImage } from '../../../hooks/use-profile-image';
import { TruckyAvatar, type TruckyState } from './TruckyAvatar';
import clsx from 'clsx';
import type { Message } from '../../../hooks/use-ai';

// ── Navigation keyword-to-route mapping ──────────────────────────────

// `labelKey` points at a translation key (reused nav.* chrome where it exists,
// with a component-scoped fallback for destinations without a nav entry). The
// visible label is resolved with t() at render time.
const NAV_ROUTES: { keywords: string[]; route: string; labelKey: string }[] = [
  { keywords: ['dashboard', 'home', 'overview'], route: '/dashboard', labelKey: 'nav.dashboard' },
  { keywords: ['dispatch', 'dispatching'], route: '/dispatch', labelKey: 'nav.dispatch' },
  { keywords: ['load', 'loads', 'shipment', 'shipments'], route: '/dispatch-loads', labelKey: 'nav.loads' },
  { keywords: ['driver', 'drivers', 'hazmat', 'hos'], route: '/drivers', labelKey: 'nav.drivers' },
  // ATL-001064: carriers were merged into the /drivers grid; /carriers no longer exists.
  { keywords: ['carrier', 'carriers'], route: '/drivers', labelKey: 'nav.drivers' },
  { keywords: ['equipment', 'fleet', 'truck', 'trailer', 'vehicle'], route: '/equipment', labelKey: 'nav.equipment' },
  { keywords: ['invoice', 'invoices', 'billing', 'accounts receivable'], route: '/invoices', labelKey: 'nav.invoices' },
  { keywords: ['settlement', 'settlements', 'driver pay', 'carrier pay'], route: '/settlements', labelKey: 'nav.settlements' },
  { keywords: ['report', 'reports', 'analytics'], route: '/reports', labelKey: 'nav.reports' },
  { keywords: ['support', 'ticket', 'tickets', 'help'], route: '/support', labelKey: 'nav.support' },
  // ATL-001064: /settings/company doesn't exist; the real settings landing is /settings.
  { keywords: ['setting', 'settings', 'preferences'], route: '/settings', labelKey: 'nav.settings' },
  { keywords: ['integration', 'integrations', 'api'], route: '/admin/integrations', labelKey: 'components.chatMessage.navIntegrations' },
];

/** Detect page references in assistant messages and return matching routes */
function detectNavLinks(text: string): { route: string; labelKey: string }[] {
  const lower = text.toLowerCase();
  const matches: { route: string; labelKey: string }[] = [];
  const seen = new Set<string>();
  for (const entry of NAV_ROUTES) {
    for (const kw of entry.keywords) {
      if (lower.includes(kw) && !seen.has(entry.route)) {
        matches.push({ route: entry.route, labelKey: entry.labelKey });
        seen.add(entry.route);
        break;
      }
    }
  }
  return matches.slice(0, 3); // max 3 links
}

interface ChatMessageProps {
  message: Message;
  /** When provided, overrides Trucky's pose on the assistant avatar.
   *  Used for the streaming message so Trucky animates while the AI
   *  is composing the response. Leave undefined on settled messages
   *  to keep them in `normal`. */
  truckyState?: TruckyState;
}

/** Simple markdown renderer — handles bold, italic, code, lists, code blocks. */
function renderMarkdown(text: string) {
  const blocks: React.ReactNode[] = [];
  const lines = text.split('\n');
  let inCodeBlock = false;
  let codeBuffer: string[] = [];
  let blockIndex = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('```')) {
      if (inCodeBlock) {
        blocks.push(
          <pre
            key={blockIndex++}
            className="bg-gray-800 text-gray-100 rounded-lg p-3 text-xs overflow-x-auto my-2 font-mono"
          >
            {codeBuffer.join('\n')}
          </pre>
        );
        codeBuffer = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeBuffer.push(line);
      continue;
    }

    if (line.trim() === '') {
      blocks.push(<div key={blockIndex++} className="h-2" />);
      continue;
    }

    blocks.push(
      <p key={blockIndex++} className="leading-relaxed">
        {renderInline(line)}
      </p>
    );
  }

  // Unclosed code block
  if (inCodeBlock && codeBuffer.length > 0) {
    blocks.push(
      <pre
        key={blockIndex++}
        className="bg-gray-800 text-gray-100 rounded-lg p-3 text-xs overflow-x-auto my-2 font-mono"
      >
        {codeBuffer.join('\n')}
      </pre>
    );
  }

  return blocks;
}

function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Regex: **bold**, *italic*, `code`, - list items
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      // Bold
      parts.push(
        <strong key={key++} className="font-semibold">
          {match[2]}
        </strong>
      );
    } else if (match[3]) {
      // Italic
      parts.push(
        <em key={key++} className="italic">
          {match[4]}
        </em>
      );
    } else if (match[5]) {
      // Inline code
      parts.push(
        <code
          key={key++}
          className="bg-gray-200 text-gray-800 rounded px-1.5 py-0.5 text-xs font-mono"
        >
          {match[6]}
        </code>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  // Check if it's a list item
  const trimmed = text.trimStart();
  if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || /^\d+\.\s/.test(trimmed)) {
    return [
      <span key="bullet" className="inline-block w-4 text-gray-400">
        {trimmed.startsWith('- ') || trimmed.startsWith('* ') ? '\u2022' : ''}
      </span>,
      ...parts,
    ];
  }

  return parts;
}

function DataQuerySection({ metadata }: { metadata: Record<string, unknown> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!metadata?.query) return null;

  return (
    <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 w-full px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {t('components.chatMessage.dataQuery')}
      </button>
      {open && (
        <div className="px-3 pb-2">
          <pre className="text-xs bg-gray-50 rounded p-2 overflow-x-auto text-gray-700 font-mono">
            {String(metadata.query)}
          </pre>
        </div>
      )}
    </div>
  );
}

function NavigationLinks({ text }: { text: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const links = detectNavLinks(text);
  if (links.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-200/60">
      {links.map((link) => (
        <button
          key={link.route}
          onClick={() => navigate(link.route)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-[#1B3050]/8 text-[#1B3050] hover:bg-[#1B3050]/15 transition-colors"
        >
          {t('components.chatMessage.takeMeTo', { label: t(link.labelKey) })}
          <ArrowRight className="h-2.5 w-2.5" />
        </button>
      ))}
    </div>
  );
}

export function ChatMessage({ message, truckyState }: ChatMessageProps) {
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  const { data: me } = useMe();
  const { profileImage } = useProfileImage();
  // Prefer the locally-uploaded profile image (localStorage data URL,
  // matches what Header renders) and fall back to the server-side
  // `avatarUrl` once that endpoint lands.
  const userAvatarUrl = profileImage ?? me?.avatar ?? null;
  const aiPose: TruckyState = truckyState ?? 'normal';

  return (
    <div
      className={clsx(
        'flex gap-2.5 mb-4',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar — user message: show the signed-in user's profile picture
          if they have one, falling back to a User icon. AI message: render
          Trucky in the normal pose. */}
      <div
        className={clsx(
          'flex-shrink-0 flex items-center justify-center h-7 w-7 rounded-full overflow-hidden',
          isUser ? 'bg-[#1B3050]' : 'bg-gray-200'
        )}
      >
        {isUser ? (
          userAvatarUrl ? (
            <img
              src={userAvatarUrl}
              alt={me?.firstName ?? t('components.chatMessage.you')}
              className="h-full w-full object-cover"
              draggable={false}
            />
          ) : (
            <User className="h-3.5 w-3.5 text-white" />
          )
        ) : (
          <TruckyAvatar state={aiPose} className="h-full w-full p-0.5" />
        )}
      </div>

      {/* Bubble */}
      <div
        className={clsx(
          'max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm',
          isUser
            ? 'bg-[#1B3050] text-white'
            : 'bg-gray-100 text-gray-900'
        )}
      >
        <div>{renderMarkdown(message.content)}</div>
        {message.metadata && (
          <DataQuerySection metadata={message.metadata} />
        )}
        {/* Navigation links for assistant messages */}
        {!isUser && <NavigationLinks text={message.content} />}
        <p
          className={clsx(
            'text-[10px] mt-1.5',
            isUser ? 'text-white/60' : 'text-gray-400'
          )}
        >
          {new Date(message.createdAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>
    </div>
  );
}
